import { z } from 'zod';
/**
 * Optional application areas that a deployment can switch off.
 *
 * A clone inherits every capability the template ships, but a given client only
 * contracted for some of them. Rather than deleting code that a later phase may
 * want — and that upstream will keep changing — each area is a flag.
 *
 * The server is the source of truth. Disabling a feature hides its navigation
 * *and* removes its route, so it is genuinely off rather than merely invisible.
 * Frontend visibility is never treated as security: the API's permission guards
 * still apply independently.
 *
 * Anything not listed here is always on. Identity, organizations, roles, and
 * audit are structural, not optional.
 *
 * `organization` is a flag because a single-client portal has exactly one
 * organization that nobody will rename. Roles, members and audit stay on: those
 * are how users get added and how access is reviewed.
 *
 * `dashboard` is a flag because the template's dashboard summarises CRM, project
 * and task counts. With those areas switched off it reports on things that are
 * not there, so a deployment that has not contracted for them should not show
 * it. When the real financial dashboard lands the flag goes back on.
 */
export const FEATURES = [
    'crm',
    'dashboard',
    'projects',
    'tasks',
    'documents',
    'reports',
    'notifications',
    'integrations',
    'organization',
    'reservations',
];
export const FeatureSchema = z.enum(FEATURES);
/**
 * Parses the deployment's feature configuration.
 *
 * Accepts a comma-separated list, `all`, or `none`. Unknown names are rejected
 * rather than ignored, so a typo in configuration fails at boot instead of
 * silently hiding half the application.
 */
export function parseFeatures(value) {
    const raw = (value ?? 'all').trim();
    if (raw === '' || raw.toLowerCase() === 'all')
        return [...FEATURES];
    if (raw.toLowerCase() === 'none')
        return [];
    const requested = raw
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '');
    const unknown = requested.filter((entry) => !FEATURES.includes(entry));
    if (unknown.length > 0) {
        throw new Error(`Unknown feature(s): ${unknown.join(', ')}. Valid features: ${FEATURES.join(', ')}`);
    }
    return [...new Set(requested)];
}
